/**
 * @file App.js
 * @description Root application component with exact hex color matching for a seamless UI.
 */

import { useEffect, useState } from "react";
import { AndroidInstallPrompt } from "./components/AndroidInstallPrompt";
import AuthModal from "./components/auth/AuthModal";
import { IOSInstallGuide } from "./components/IOSInstallGuide";
import { DailyQuoteModal } from "./components/shared/DailyQuoteModal";
import Navbar from "./components/shared/Navbar";
import { PaymentSuccessToast } from "./components/shared/PaymentSuccessToast";

import { PullToRefreshIndicator } from "./components/shared/PullToRefreshIndicator";
import { WeeklyMilestoneModal } from "./components/shared/WeeklyMilestoneModal";
import { usePullToRefresh } from "./hooks/usePullToRefresh";
import { usePWAInstall } from "./hooks/usePWAInstall";
import LogScreen from "./screens/LogScreen";
import { AuthProvider } from "./utils/AuthContext";
import { useHabitStreak } from "./hooks/useHabitStreak";
import { APP_VERSION } from "./constants/version";

export default function App() {
  const [showAuth, setShowAuth] = useState(false);
  const [isPullToRefreshEnabled, setIsPullToRefreshEnabled] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("payment");
  });

  const {
    showAndroidPrompt,
    isInstalled,
    installApp,
    dismissAndroidPrompt,
    canInstall,
  } = usePWAInstall();
  const { streak, milestone, dismissMilestone } = useHabitStreak();

  // Clean up URL params and auto-dismiss payment toast
  useEffect(() => {
    if (!paymentStatus) return;
    window.history.replaceState({}, "", window.location.pathname);
    const timer = setTimeout(() => setPaymentStatus(null), 5000);
    return () => clearTimeout(timer);
  }, [paymentStatus]);

  const handleRefresh = async () => {
    try {
      // Clear all caches
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map((cacheName) => caches.delete(cacheName)),
      );

      // Unregister all service workers
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(
        registrations.map((registration) => registration.unregister()),
      );

      // Hard refresh the page to clear everything
      window.location.reload(true);
    } catch (error) {
      console.error("Refresh failed:", error);
      // Fallback to simple reload
      window.location.reload();
    }
  };

  const { pullDistance, isRefreshing } = usePullToRefresh(
    handleRefresh,
    80,
    isPullToRefreshEnabled && !showAuth,
  );

  return (
    <AuthProvider>
      {/* Pull to refresh indicator */}
      <PullToRefreshIndicator
        pullDistance={pullDistance}
        isRefreshing={isRefreshing}
        threshold={80}
      />

      {/* COLOR FIX: Setting the exact hex #0B0C0E matches your Navbar.
          Using 'fixed' inset ensures the background color covers the entire 
          browser viewport regardless of content length.
      */}
      <div className="min-h-screen bg-[#0B0B0B] text-white selection:bg-brand-green/30">
        <Navbar onShowAuth={() => setShowAuth(true)} />
        <main className="pb-20">
          <LogScreen
            onShowAuth={() => setShowAuth(true)}
            setIsPullToRefreshEnabled={setIsPullToRefreshEnabled}
          />
          <footer className="pb-6 text-center">
            <span className="text-[10px] text-white/20 font-medium tracking-wider">
              v{APP_VERSION}
            </span>
          </footer>
        </main>
        {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
        <IOSInstallGuide />
        <AndroidInstallPrompt
          onInstall={installApp}
          onDismiss={dismissAndroidPrompt}
          canInstall={showAndroidPrompt && canInstall && !isInstalled}
        />
        <DailyQuoteModal />
        {milestone && (
          <WeeklyMilestoneModal
            milestone={milestone}
            streak={streak}
            onDismiss={dismissMilestone}
          />
        )}
        {paymentStatus && (
          <PaymentSuccessToast
            status={paymentStatus}
            onDismiss={() => setPaymentStatus(null)}
          />
        )}
      </div>
    </AuthProvider>
  );
}
